import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  SystemStyleObject,
  useBreakpointValue,
  useTheme,
} from '@chakra-ui/react';
import { ReactNode, RefObject } from 'react';
import {
  ContentEditable,
  ContentEditableHandler,
  usePlainTextPaste,
} from 'src/components/content-editable';
import BodyPadding from 'src/components/layout/body-padding';
import { Theme } from 'src/theme';
import { EDITOR_CONFIG } from './config';

export interface EditorProps extends FlexProps {
  titleRef?: RefObject<ContentEditableHandler>;
  title?: string;
  titleProps?: BoxProps;
  showTitle?: boolean;
  children?: ReactNode;
  editorChildren?: ReactNode;
  readOnly?: boolean;
  editorBg?: string;
  editorSx?: SystemStyleObject;
  full?: boolean;
  holderId?: string;
  minHeight?: string;
}

export function Editor({
  title,
  titleRef,
  children,
  readOnly,
  editorBg = 'white',
  editorSx,
  full = false,
  titleProps,
  editorChildren,
  holderId,
  minHeight,
  showTitle = true,
  ...props
}: EditorProps) {
  usePlainTextPaste(titleRef?.current?.getRef());

  return (
    <BodyPadding px={full ? 0 : EDITOR_CONFIG.px} py={full ? 0 : 6} {...props}>
      <Flex position="relative" direction="column" width="100%">
        {children}
        <Flex
          display="inline-flex"
          minWidth="100%"
          bg={editorBg}
          borderRadius="4px"
          zIndex={0}
          flex={1}
        >
          <Box width="100%" minWidth={0}>
            {!readOnly && showTitle && (
              <ContentEditable
                singleLine
                fontSize={{
                  base: '2xl',
                  md: '3xl',
                }}
                maxLen={50}
                ref={titleRef as any}
                defaultValue={title}
                boxSizing="content-box"
                maxWidth={EDITOR_CONFIG.articleMaxWidth}
                m="0 auto"
                pl="2rem"
                pr="3.75rem"
                pt="2.5rem"
                fontWeight="bold"
                lineHeight="48px"
                pb={{
                  base: 0,
                  md: 0,
                }}
                placeholder="Heading"
                {...titleProps}
              />
            )}
            <Box
              className={readOnly ? 'read-only' : ''}
              position="relative"
              id={holderId || 'editor'}
              bg={editorBg}
              pl={full ? 0 : 5}
              pr={full ? 0 : '3.75rem'}
              py={full ? 0 : 3}
              zIndex={0}
              m="auto"
              maxWidth={EDITOR_CONFIG.maxWidth}
              minHeight={
                minHeight || (readOnly ? 'unset' : 'calc(100vh - 250px)')
              }
              sx={editorSx}
            >
              {editorChildren}
            </Box>
          </Box>
        </Flex>
      </Flex>
      <GlobalEditorStyle full={full} />
    </BodyPadding>
  );
}

function GlobalEditorStyle(props: { full?: boolean }) {
  const theme = useTheme<Theme>();
  const { bodyQuote, bodyQuote0, body4 } = theme.textStyles;
  const isMobile = useBreakpointValue({
    base: true,
    ml: false,
  });
  const quote = useBreakpointValue({
    base: bodyQuote0,
    md: bodyQuote,
  });

  const isMobileFull = isMobile && !!props.full;

  return (
    <style jsx global>{`
      .ce-header,
      .cdx-quote,
      .ce-paragraph,
      .link-tool,
      .cdx-list {
        padding-left: ${isMobileFull ? '24px !important' : 0};
        padding-right: ${isMobileFull ? '24px' : 0};
        max-width: ${EDITOR_CONFIG.articleMaxWidth};
        margin: auto !important;
      }
      .ce-paragraph[data-placeholder]:empty::before {
        color: ${theme.colors.gray['300']};
      }
      .cdx-list {
        position: relative;
        left: 18px;
      }
      .ce-paragraph {
        font-size: ${theme.fontSizes[body4.fontSize]};
        line-height: ${body4.lineHeight};
        letter-spacing: ${body4.letterSpacing};
      }
      .cdx-block,
      .cdx-list__item {
        padding-left: 0;
      }

      .ce-toolbar .ce-toolbox {
        border-radius: 4px;
        overflow: hidden;
      }
      .cdx-quote__text {
        min-height: unset !important;
        border: none;
        border-left: 2px solid ${theme.colors.gray['900']};
        box-shadow: none;
        border-radius: 0;
        margin-bottom: 0;
        font-size: ${theme.fontSizes[quote?.fontSize] || '20px'};
        line-height: ${quote?.lineHeight || '32px'};
        font-style: italic;
        color: ${theme.colors.gray['900']};
        padding-left: 16px;
      }
      .cdx-quote__caption {
        display: none;
      }
      .ce-inline-tool,
      .ce-inline-toolbar__dropdown:hover,
      .ce-conversion-toolbar,
      .ce-toolbar .ce-toolbox,
      .ce-inline-tool:hover,
      .ce-conversion-tool:hover,
      .ce-conversion-tool__icon,
      .ce-settings,
      .cdx-settings-button,
      .cdx-settings-button:hover,
      .ce-settings__button,
      .ce-settings__button:hover,
      .ce-toolbox__button,
      .ce-inline-toolbar {
        background-color: ${theme.colors.gray['900']};
        color: #dadada;
      }
      .ce-block__content {
        max-width: ${EDITOR_CONFIG.articleMaxWidth};
      }
      .ce-toolbar__content {
        max-width: ${EDITOR_CONFIG.articleMaxWidth};
      }
      .codex-editor__redactor {
        margin-right: 0px !important;
      }
      .read-only
        .codex-editor--empty
        .ce-block:first-child
        .ce-paragraph[data-placeholder]:empty::before {
        opacity: 0;
      }
      .ce-toolbar__plus--hidden {
        display: inline-flex;
      }
      .cdx-block,
      .ce-header {
        padding-top: 16px;
        padding-bottom: 16px;
      }
      .image-tool__caption:focus {
        display: block !important;
      }
    `}</style>
  );
}
